import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Lock, RefreshCw, LogOut, Package } from "lucide-react";
import api from "@/lib/api";

const STATUS_STYLES = {
  placed: "border-white/25 text-[#A3A3A3]",
  paid: "border-[#00ff72]/60 text-[#00ff72]",
  shipped: "border-white/10 text-[#666666]",
};

export default function Admin() {
  const [pin, setPin] = useState(() => sessionStorage.getItem("sfh_admin_pin") || "");
  const [pinInput, setPinInput] = useState("");
  const [orders, setOrders] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [codes, setCodes] = useState([]);
  const [codeInput, setCodeInput] = useState("");
  const [referrerInput, setReferrerInput] = useState("");
  const [codeError, setCodeError] = useState("");
  const [howFound, setHowFound] = useState({ counts: {}, submissions: [] });

  const loadHowFound = useCallback(async (pinValue) => {
    try {
      const res = await api.get("/how-found", { headers: { "X-Admin-PIN": pinValue } });
      setHowFound(res.data);
    } catch {
      /* PIN gate already handled by loadOrders */
    }
  }, []);

  const loadCodes = useCallback(async (pinValue) => {
    try {
      const res = await api.get("/referral-codes", { headers: { "X-Admin-PIN": pinValue } });
      setCodes(res.data);
    } catch {
      /* PIN gate already handled by loadOrders */
    }
  }, []);

  const addCode = async (e) => {
    e.preventDefault();
    setCodeError("");
    try {
      await api.post(
        "/referral-codes",
        { code: codeInput, referrer: referrerInput },
        { headers: { "X-Admin-PIN": pin } }
      );
      setCodeInput("");
      setReferrerInput("");
      loadCodes(pin);
    } catch (err) {
      setCodeError(err.response?.data?.detail || "Could not add code.");
    }
  };

  const deleteCode = async (id) => {
    try {
      await api.delete(`/referral-codes/${id}`, { headers: { "X-Admin-PIN": pin } });
      loadCodes(pin);
    } catch {
      setCodeError("Could not delete code.");
    }
  };

  const loadOrders = useCallback(
    async (pinValue) => {
      setLoading(true);
      setError("");
      try {
        const res = await api.get("/orders", { headers: { "X-Admin-PIN": pinValue } });
        setOrders(res.data);
        sessionStorage.setItem("sfh_admin_pin", pinValue);
        setPin(pinValue);
        loadCodes(pinValue);
        loadHowFound(pinValue);
      } catch {
        setError("Invalid PIN. Try again.");
        sessionStorage.removeItem("sfh_admin_pin");
        setPin("");
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (pin) loadOrders(pin);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const logout = () => {
    sessionStorage.removeItem("sfh_admin_pin");
    setPin("");
    setOrders([]);
  };

  const advanceStatus = async (order) => {
    const next = order.status === "paid" ? "shipped" : "paid";
    try {
      await api.patch(`/orders/${order.id}/status`, { status: next }, { headers: { "X-Admin-PIN": pin } });
      setOrders((prev) => prev.map((o) => (o.id === order.id ? { ...o, status: next } : o)));
    } catch {
      setError("Could not update order status.");
    }
  };

  const deleteOrder = async (order) => {
    if (!window.confirm(`Delete order #${order.id.slice(0, 8).toUpperCase()} (${order.customer.name})? This cannot be undone.`)) return;
    try {
      await api.delete(`/orders/${order.id}`, { headers: { "X-Admin-PIN": pin } });
      setOrders((prev) => prev.filter((o) => o.id !== order.id));
    } catch {
      setError("Could not delete order.");
    }
  };

  const [filter, setFilter] = useState("all");

  const filteredOrders = orders.filter((o) =>
    filter === "all" ? true : (o.status || "placed") === filter
  );

  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const totalToday = orders
    .filter((o) => new Date(o.created_at) >= startOfToday)
    .reduce((s, o) => s + o.total, 0);
  const totalMonth = orders
    .filter((o) => new Date(o.created_at) >= startOfMonth)
    .reduce((s, o) => s + o.total, 0);
  const counts = {
    placed: orders.filter((o) => (o.status || "placed") === "placed").length,
    paid: orders.filter((o) => o.status === "paid").length,
    shipped: orders.filter((o) => o.status === "shipped").length,
  };

  if (!pin) {
    return (
      <main className="flex min-h-screen items-center justify-center px-6 pt-24">
        <motion.form
          data-testid="admin-pin-form"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          onSubmit={(e) => {
            e.preventDefault();
            loadOrders(pinInput);
          }}
          className="w-full max-w-sm border border-white/15 bg-[#0A0A0A] p-10"
        >
          <Lock className="h-6 w-6 text-[#00ff72]" />
          <h1 className="mt-4 font-display text-3xl text-[#F9F6F0]">Owner Access</h1>
          <p className="mt-2 font-mono text-[10px] tracking-[0.2em] text-[#666666]">
            ENTER YOUR ADMIN PIN TO VIEW ORDERS
          </p>
          <input
            data-testid="admin-pin-input"
            type="password"
            value={pinInput}
            onChange={(e) => setPinInput(e.target.value)}
            placeholder="ADMIN PIN"
            className="mt-6 w-full border border-white/15 bg-transparent px-4 py-4 font-mono text-sm text-[#F9F6F0] placeholder:text-[#666666] focus:border-[#00ff72] focus:outline-none"
          />
          {error && (
            <p data-testid="admin-pin-error" className="mt-3 font-mono text-xs text-red-400">
              {error}
            </p>
          )}
          <button
            data-testid="admin-login-btn"
            type="submit"
            disabled={loading}
            className="mt-5 w-full bg-[#00ff72] px-6 py-4 font-mono text-xs tracking-[0.2em] text-black transition-colors hover:bg-[#F9F6F0] disabled:opacity-50"
          >
            {loading ? "CHECKING..." : "VIEW ORDERS"}
          </button>
        </motion.form>
      </main>
    );
  }

  return (
    <main className="pt-24">
      <div className="mx-auto max-w-6xl px-6 py-12">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="font-mono text-[11px] tracking-[0.35em] text-[#00ff72]">OWNER DASHBOARD</p>
            <h1 className="mt-2 font-display text-5xl tracking-tight text-[#F9F6F0] md:text-6xl">
              Order <span className="italic text-[#666666]">Inbox</span>
            </h1>
          </div>
          <div className="flex gap-3">
            <button
              data-testid="admin-refresh-btn"
              onClick={() => loadOrders(pin)}
              className="flex items-center gap-2 border border-white/20 px-4 py-2 font-mono text-[10px] tracking-[0.2em] text-[#A3A3A3] transition-colors hover:border-[#00ff72] hover:text-[#00ff72]"
            >
              <RefreshCw className="h-4 w-4" /> REFRESH
            </button>
            <button
              data-testid="admin-logout-btn"
              onClick={logout}
              className="flex items-center gap-2 border border-white/20 px-4 py-2 font-mono text-[10px] tracking-[0.2em] text-[#A3A3A3] transition-colors hover:border-red-500/60 hover:text-red-400"
            >
              <LogOut className="h-4 w-4" /> LOG OUT
            </button>
          </div>
        </div>

        <div data-testid="referral-codes-section" className="mt-10 border border-white/10 bg-[#0A0A0A] p-6">
          <p className="font-mono text-[10px] tracking-[0.3em] text-[#A3A3A3]">REFERRAL CODES — 10% OFF FOR THE CUSTOMER, 10% COMMISSION FOR YOUR FRIEND</p>
          <form onSubmit={addCode} className="mt-4 flex flex-wrap gap-3">
            <input
              data-testid="new-code-input"
              value={codeInput}
              onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
              placeholder="CODE (E.G. MIKE10)"
              className="w-48 border border-white/15 bg-transparent px-4 py-3 font-mono text-xs text-[#F9F6F0] placeholder:text-[#666666] focus:border-[#00ff72] focus:outline-none"
            />
            <input
              data-testid="new-referrer-input"
              value={referrerInput}
              onChange={(e) => setReferrerInput(e.target.value)}
              placeholder="FRIEND'S NAME"
              className="w-48 border border-white/15 bg-transparent px-4 py-3 font-mono text-xs text-[#F9F6F0] placeholder:text-[#666666] focus:border-[#00ff72] focus:outline-none"
            />
            <button
              data-testid="add-code-btn"
              type="submit"
              className="bg-[#00ff72] px-6 py-3 font-mono text-[10px] tracking-[0.2em] text-black transition-colors hover:bg-[#F9F6F0]"
            >
              ADD CODE
            </button>
          </form>
          {codeError && (
            <p data-testid="code-error" className="mt-3 font-mono text-xs text-red-400">{codeError}</p>
          )}
          <div className="mt-5 space-y-2">
            {codes.length === 0 && (
              <p className="font-mono text-xs text-[#666666]">No codes yet — add your first one above.</p>
            )}
            {codes.map((c) => (
              <div
                key={c.id}
                data-testid={`referral-code-${c.code}`}
                className="flex flex-wrap items-center justify-between gap-3 border border-white/10 px-4 py-3"
              >
                <div className="flex flex-wrap items-center gap-4">
                  <span className="font-mono text-sm tracking-widest text-[#00ff72]">{c.code}</span>
                  <span className="font-mono text-xs text-[#A3A3A3]">{c.referrer}</span>
                  <span className="font-mono text-[10px] text-[#666666]">
                    {c.uses} ORDER{c.uses === 1 ? "" : "S"} · COMMISSION EARNED: ${(c.commission || 0).toFixed(2)}
                  </span>
                </div>
                <button
                  data-testid={`delete-code-${c.code}`}
                  onClick={() => deleteCode(c.id)}
                  className="font-mono text-[10px] tracking-[0.2em] text-[#666666] transition-colors hover:text-red-400"
                >
                  DELETE
                </button>
              </div>
            ))}
          </div>
        </div>

        <div data-testid="how-found-section" className="mt-10 border border-white/10 bg-[#0A0A0A] p-6">
          <p className="font-mono text-[10px] tracking-[0.3em] text-[#A3A3A3]">
            HOW DID YOU FIND US — {howFound.submissions.length} RESPONSE{howFound.submissions.length === 1 ? "" : "S"}
          </p>
          <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3">
            {Object.entries(howFound.counts).map(([source, count]) => (
              <div key={source} data-testid={`how-found-count-${source}`} className="border border-white/10 px-3 py-2">
                <p className="font-mono text-[10px] tracking-widest text-[#666666]">{source.toUpperCase()}</p>
                <p className="mt-1 font-display text-xl text-[#00ff72]">{count}</p>
              </div>
            ))}
          </div>
          <div className="mt-5 space-y-2">
            {howFound.submissions.length === 0 && (
              <p className="font-mono text-xs text-[#666666]">No responses yet. Share yoursite.com/how-did-you-find-us to start collecting.</p>
            )}
            {howFound.submissions.map((s, i) => (
              <div key={i} className="flex flex-wrap items-center justify-between gap-2 border border-white/10 px-4 py-3">
                <span className="font-mono text-xs text-[#F9F6F0]">
                  {s.source}
                  {s.other && <span className="text-[#A3A3A3]"> — "{s.other}"</span>}
                </span>
                <span className="font-mono text-[10px] text-[#666666]">
                  {new Date(s.created_at).toLocaleString("en-US")}
                </span>
              </div>
            ))}
          </div>
        </div>

        {orders.length > 0 && (
          <>
            <div className="mt-10 grid grid-cols-2 gap-px border border-white/10 bg-white/10 md:grid-cols-4">
              {[
                { label: "TODAY", value: `$${totalToday.toFixed(2)}` },
                { label: "THIS MONTH", value: `$${totalMonth.toFixed(2)}` },
                { label: "TOTAL ORDERS", value: String(orders.length) },
                { label: "READY TO SHIP", value: String(counts.paid) },
              ].map((s) => (
                <div key={s.label} className="bg-[#0A0A0A] p-5">
                  <p className="font-mono text-[10px] tracking-[0.25em] text-[#666666]">{s.label}</p>
                  <p
                    data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="mt-2 font-display text-3xl text-[#F9F6F0]"
                  >
                    {s.value}
                  </p>
                </div>
              ))}
            </div>
            <div className="mt-6 flex flex-wrap gap-2">
              {[
                { key: "all", label: `ALL (${orders.length})` },
                { key: "placed", label: `NEW (${counts.placed})` },
                { key: "paid", label: `PAID (${counts.paid})` },
                { key: "shipped", label: `SHIPPED (${counts.shipped})` },
              ].map((t) => (
                <button
                  key={t.key}
                  data-testid={`filter-tab-${t.key}`}
                  onClick={() => setFilter(t.key)}
                  className={`border px-4 py-2 font-mono text-[10px] tracking-[0.2em] transition-colors ${
                    filter === t.key
                      ? "border-[#00ff72] bg-[#00ff72] text-black"
                      : "border-white/15 text-[#A3A3A3] hover:border-white/40 hover:text-[#F9F6F0]"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </>
        )}

        {orders.length === 0 ? (
          <div data-testid="admin-empty-state" className="mt-16 border border-white/10 py-24 text-center">
            <Package className="mx-auto h-8 w-8 text-[#666666]" />
            <p className="mt-4 font-display text-2xl text-[#A3A3A3]">No orders yet</p>
          </div>
        ) : filteredOrders.length === 0 ? (
          <div data-testid="admin-filter-empty" className="mt-8 border border-white/10 py-16 text-center">
            <p className="font-display text-2xl text-[#A3A3A3]">No orders with this status</p>
          </div>
        ) : (
          <div className="mt-6 space-y-4">
            {filteredOrders.map((o) => (
              <motion.div
                key={o.id}
                data-testid={`admin-order-${o.id.slice(0, 8)}`}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                className="border border-white/10 bg-[#0A0A0A] p-6"
              >
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <p className="font-mono text-xs tracking-[0.2em] text-[#00ff72]">
                      ORDER #{o.id.slice(0, 8).toUpperCase()}
                    </p>
                    <p className="mt-1 font-mono text-[10px] tracking-widest text-[#666666]">
                      {new Date(o.created_at).toLocaleString()}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <p className="font-display text-2xl text-[#F9F6F0]">${o.total.toFixed(2)}</p>
                    <div className="flex items-center gap-2">
                      <span
                        data-testid={`order-status-${o.id.slice(0, 8)}`}
                        className={`border px-2 py-1 font-mono text-[10px] tracking-[0.2em] ${STATUS_STYLES[o.status] || STATUS_STYLES.placed}`}
                      >
                        {(o.status || "placed").toUpperCase()}
                      </span>
                      {o.status !== "shipped" && (
                        <button
                          data-testid={`order-advance-${o.id.slice(0, 8)}`}
                          onClick={() => advanceStatus(o)}
                          className="border border-[#00ff72]/50 px-2 py-1 font-mono text-[10px] tracking-[0.2em] text-[#00ff72] transition-colors hover:bg-[#00ff72] hover:text-black"
                        >
                          {o.status === "paid" ? "MARK SHIPPED" : "MARK PAID"}
                        </button>
                      )}
                      <button
                        data-testid={`order-delete-${o.id.slice(0, 8)}`}
                        onClick={() => deleteOrder(o)}
                        className="border border-red-500/40 px-2 py-1 font-mono text-[10px] tracking-[0.2em] text-red-400 transition-colors hover:bg-red-500 hover:text-black"
                      >
                        DELETE
                      </button>
                    </div>
                  </div>
                </div>
                <div className="mt-4 grid gap-6 border-t border-white/10 pt-4 md:grid-cols-2">
                  <div>
                    <p className="font-mono text-[10px] tracking-[0.25em] text-[#666666]">CUSTOMER</p>
                    <p className="mt-2 text-sm text-[#F9F6F0]">{o.customer.name}</p>
                    <p className="font-mono text-xs text-[#A3A3A3]">{o.customer.email}</p>
                    {o.customer.institution && (
                      <p className="font-mono text-xs text-[#A3A3A3]">{o.customer.institution}</p>
                    )}
                    <p className="mt-1 font-mono text-xs text-[#666666]">
                      {o.customer.address}
                      {(o.customer.city || o.customer.state || o.customer.zip) &&
                        `, ${o.customer.city}, ${o.customer.state} ${o.customer.zip}`}
                    </p>
                    {o.customer.referral && (
                      <p data-testid="order-referral" className="mt-2 font-mono text-xs tracking-widest text-[#00ff72]">
                        REFERRED BY: {o.customer.referral}{o.referrer ? ` (${o.referrer})` : ""}
                      </p>
                    )}
                    {o.discount > 0 && (
                      <p data-testid="order-commission" className="mt-1 font-mono text-[10px] tracking-widest text-[#A3A3A3]">
                        10% OFF GIVEN: -${o.discount.toFixed(2)} · COMMISSION DUE: ${(o.total * 0.10).toFixed(2)}
                      </p>
                    )}
                  </div>
                  <div>
                    <p className="font-mono text-[10px] tracking-[0.25em] text-[#666666]">ITEMS TO SHIP</p>
                    <ul className="mt-2 space-y-1">
                      {o.items.map((i) => (
                        <li key={`${i.product_id}-${i.dosage}`} className="flex justify-between font-mono text-xs text-[#A3A3A3]">
                          <span>
                            {i.name} — {i.dosage} x{i.qty}
                          </span>
                          <span className="text-[#F9F6F0]">${(i.price * i.qty).toFixed(2)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
